import _ from "lodash";
import React from "react";

// design system
import {
  LanguageOutlined,
  OpenBookStarOutlined,
  AttachmentClipOutlined,
} from "@toddle-edu/ds-icons";

// utils
import {
  getFieldValue,
  getIsUnitPlanFieldDisabled,
  getResolvedMinimalTreeSet,
  getMYPObjectiveLevelFromSubject,
  getIsMYPInterdisciplinaryUnit,
} from "Assessments/modules/Utils";
import {
  extractContentFromHTML,
  getAttachmentUrlFromFieldValue,
  htmlToText,
} from "Utils";
import {
  getDefaultUnitPlanImage,
  getElementValue,
} from "UnitPlans/modules/Utils";
import { getUnitPlanDetailsValueFromFieldKey } from "IBPlanner/modules/IBPlannerModule";
import {
  getPropertyValue,
  getPlannerFieldOptions as getPlannerFieldV2Options,
} from "Fields/FieldUtils";
import { getOptionsForUnitPlanDetailsField } from "UnitPlanDetails/routes/IBPlanner/modules/Utils";

import { wrapPlaceholderWithSpan } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/components/PromptCards/PromptCards";

// constants
import {
  ASSESSMENT,
  UNIT,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import {
  ELEMENT_TYPE_ADD_LEARNING_GOALS,
  INTERDISCIPLINARY_FIELD_MAPPING,
  MYP_OBJECTIVES,
  SHOW_COUNT_FIELDS,
} from "./Constants";

const styles = {
  uiBaseContainerStyle: {
    height: "100%",
  },
};

export const isOnlySpanTag = inputString => {
  const regex = /^\s*<span\b[^>]*>([\s\S]*?)<\/span>(\s|&nbsp;)?$/;
  return regex.test(inputString);
};

export const getFooterOptions = params => {
  const {
    isQuestionAssistant,
    conversationAttachments,
    isSuggestionLibraryDisabled,
    isAITutor,
    isAttachmentUploadDisabled,
  } = params;

  return [
    {
      id: "add_attachment",
      label: "ai:attach",
      icon: <AttachmentClipOutlined />,
      isVisible: !isAttachmentUploadDisabled,
      tooltip: "ai:attach_button_tooltip_msg",
      disabled: _.size(conversationAttachments) >= 5,
      disabledTooltip: "ai:you_can_only_upload_5_files_msg",
      showTooltip: true,
    },
    {
      id: "language",
      label: "common:language",
      icon: <LanguageOutlined />,
      showTooltip: true,
    },
    {
      id: "suggestion_library",
      label: isAITutor ? "ai:template_library" : "common:suggestion_library",
      icon: <OpenBookStarOutlined />,
      isVisible: !isQuestionAssistant && !isSuggestionLibraryDisabled,
      showTooltip: true,
    },
  ];
};

export const shouldRemoveBrTagFromEnd = ({ selectedAction, ref }) => {
  const childNodes = ref?.childNodes;
  /**
   * On space key press, a br tag is appended at the end in firefox browser
   * when there is no selectedAction, length of childNodes is 2 and last child is br tag if space is given
   * So, _.isEmpty(selectedAction) is required, otherwise space will be removed
   */
  return (
    !_.isEmpty(selectedAction) &&
    _.size(childNodes) === 2 &&
    _.last(ref.childNodes)?.nodeName === "BR"
  );
};

export const getModalTitle = params => {
  const {
    t,
    field,
    fieldList,
    unitPlanFields,
    isUnitDependent,
    assessmentTypeLabel,
    stepList,
    fieldConfig,
    isUnitPlanAI,
    shouldConvertNonPortedFieldsToPortedFlag = false,
  } = params;

  let unitTitle = getFieldValue({
    fields: unitPlanFields,
    uid: "title",
  });

  if (_.isEmpty(unitTitle)) {
    unitTitle = t("common:untitled");
  }

  if (field === "subjects" && isUnitPlanAI) {
    return t("common:add_label1_for_label2_with_quotes", {
      label1: t("common:subject_plural"),
      label2: unitTitle,
    });
  }

  switch (field) {
    case "title":
      return t("common:add_label1_for_label2_without_quotes", {
        label1: t("common:title"),
        label2: t("common:lowercase", {
          text: isUnitDependent ? t("common:unit") : t(assessmentTypeLabel),
        }),
      });
    case "unitPlan":
    case "unitTitle":
      return t("common:select_with_label", {
        label: t("common:unit"),
      });
    case "centralIdea":
      return t("common:add_label1_for_label2_with_quotes", {
        label1: t("common:centralIdea_label"),
        label2: unitTitle,
      });
    case "soi":
      return t("common:add_label1_for_label2_with_quotes", {
        label1: t("unitPlanTemplate:unitPlan_MYP_SOI_label"),
        label2: unitTitle,
      });
    case "globalContextMYP":
      return t("common:select_label1_for_label2", {
        label1: t("unitPlanTemplate:unitPlan_globalContextMYP_label"),
        label2: unitTitle,
      });
    case "content":
    case "disciplinaryContent":
      return t("common:add_label1_for_label2_with_quotes", {
        label1: t("common:content_label"),
        label2: unitTitle,
      });
    case "inquiryQuestions": {
      return t("common:add_label1_for_label2_with_quotes", {
        label1: t("common:inquiry_questions"),
        label2: unitTitle,
      });
    }
    case "dateDuration": {
      return t("common:update_with_label", {
        label: _.lowerCase(t("common:duration")),
      });
    }
    default: {
      const fieldObj = _.get(fieldList, field, {});
      let fieldLabel = _.get(fieldObj, `config.label`);
      if (shouldConvertNonPortedFieldsToPortedFlag) {
        fieldLabel = getPropertyValue({
          propertyPath: "label",
          fieldObj,
        });
      }
      const stepLabel = _.get(stepList, `${field}.label`);

      if (!_.isEmpty(fieldConfig)) {
        const { modalLabel, options, label } = fieldConfig || {};

        if (!_.isEmpty(modalLabel)) return modalLabel;
        else if (!_.isEmpty(options)) {
          return t("common:select_with_label", { label });
        } else {
          return t("common:add_with_label", {
            label,
          });
        }
      } else if (fieldLabel) {
        return t("common:add_with_label", { label: fieldLabel });
      } else if (stepLabel) {
        return t("common:add_with_label", { label: stepLabel });
      } else {
        return t("common:add");
      }
    }
  }
};

export const getTypeToRender = params => {
  const {
    fields,
    fieldList,
    assessmentFieldList,
    unitPlanFieldList,
    isUnitDependent,
    shouldConvertNonPortedFieldsToPortedFlag = false,
  } = params;

  const { field, config } = _.first(fields) || {};
  const type = _.get(config, "type");

  // if type is specified in the field config then render it
  if (type) return type;

  // fields with InputNumberList type should render with it's own type (lois etc.)
  const isInputNumberList =
    _.get(unitPlanFieldList, `${field}.type`) === "InputNumberList";

  if (isUnitDependent && isInputNumberList) {
    return _.get(unitPlanFieldList, `${field}.type`);
  }

  // render learning goals (both unit plans (except few) and assessments) through AddLearningGoals
  const learningGoalsOptions = _.get(
    assessmentFieldList,
    `addLearningGoals.config.options`,
    []
  );

  const learningGoalsElementKeys = _.map(learningGoalsOptions, "key");

  if (_.includes(learningGoalsElementKeys, field)) {
    return ELEMENT_TYPE_ADD_LEARNING_GOALS;
  }

  const fieldObj = _.get(fieldList, field, {});

  const fieldType = shouldConvertNonPortedFieldsToPortedFlag
    ? `${getPropertyValue({
        propertyPath: "uiType",
        fieldObj,
      })}`
    : _.get(fieldObj, `type`);

  return shouldConvertNonPortedFieldsToPortedFlag && fieldType
    ? `${fieldType}_Ported`
    : fieldType;
};

export const getDependentFields = params => {
  const {
    fieldList,
    field,
    shouldConvertNonPortedFieldsToPortedFlag = false,
  } = params;

  const keys = [];
  if (shouldConvertNonPortedFieldsToPortedFlag) {
    _.forEach(fieldList, (item, key) => {
      const plannerElementConfig = getPropertyValue({
        propertyPath: "plannerElementObj",
        fieldObj: item,
      });
      const isFilteredBy = _.includes(plannerElementConfig?.filteredBy, field);

      if (isFilteredBy) keys.push(key);
    });
  } else {
    _.forEach(fieldList, (item, key) => {
      const config = _.get(item, "config", {});
      const isFilteredBy = _.includes(
        config?.plannerElementConfig?.filteredBy,
        field
      );

      if (isFilteredBy) keys.push(key);
    });
  }

  return keys;
};

export const getFieldSelectionModalWidth = type => {
  if (!type) return "fit-content";

  const normalizedType = type.replace(/_Ported$/, "");
  switch (normalizedType) {
    case "RichText":
    case "IBTheme":
    case "MultiSelectWithIcon":
    case "MultiField":
    case "InputNumberList":
      return "648px";
    case "IBMultiSelect":
      return "720px";
    case "SubjectGroupSelectV2":
      return "560px";
    case "MultiSelect":
      return "fit-content";
    case "GenericCardList":
    case "GenericCheckList":
    case "IBRelatedConceptsV2":
      return "70%";
    default:
      return "472px";
  }
};

export const getFieldSelectionModalHeight = ({
  type,
  fieldUID,
  isUnitDependent,
}) => {
  const normalizedType = type?.replace(/_Ported$/, "");
  switch (normalizedType) {
    case "FieldDropdown":
      if (
        (fieldUID === "subjects" || fieldUID === "unitPlan") &&
        !isUnitDependent
      ) {
        return "590px";
      } else {
        return "480px";
      }
    case "LearningIntentions":
      return "480px";
    case "IBRelatedConceptsV2":
      return "60%";
    default:
      return "fit-content";
  }
};

export const getFieldComponentProps = params => {
  const {
    t,
    type,
    value,
    field,
    allFields,
    defaultOptions,
    fieldTypeConfig,
    isUnitDependent,
    unitPlanFields,
    fieldList,
    unitPlanTemplate,
    unitPlanDetails,
    updateParams,
  } = params;

  const subFields = getSubFields({
    t,
    unitPlanFields,
    fieldList,
    field,
    unitPlanTemplate,
  });

  const uiType = field === "unitPlan" ? "LIST" : null;

  const props = {
    mode: "edit",
    fieldKey: field,
    name: field,
    value,
    type,
    assessmentFields: allFields,
    resourceFields: allFields,
    uiType,
    applyDefaultStyles: false,
    isRequired: false,
    showReflection: false,
    isLocalUpdate: true,
    debounceDuration: 0,
    subFields,
    shouldConsiderFieldValue: isUnitDependent,
    containerStyle:
      field === "unitPlan" ? styles.uiBaseContainerStyle : undefined,
  };

  const shouldInitOptions = _.get(fieldTypeConfig, "shouldInitOptions", false);

  switch (field) {
    case "pedagogicalApproachDP": {
      if (_.every(value, item => _.has(item, "value"))) {
        const _value = _.map(
          _.filter(value, item => _.get(item, "isSelected", false)),
          "id"
        );
        _.set(props, "value", _value);
      }
      break;
    }
    case "title": {
      _.set(props, "title", t("common:title"));
      _.set(props, "type", "InputField");
      break;
    }
    case "disciplinaryContent": {
      _.set(props, "label", "");
      _.set(props, "subtext", "");
      break;
    }
    case "concepts": {
      _.set(props, "fieldTypeConfig", {
        ...fieldTypeConfig,
        showTextBoxOnSelect: false,
        showSubtextUnderInfo: false,
      });
      break;
    }
    case "subjects": {
      _.set(props, "isObjectiveLevelsHidden", true);
      _.set(props, "subjectTitleStyles", { flexShrink: 0 });
      break;
    }
    case "dateDuration": {
      const fieldObj = _.get(fieldList, field, {});

      _.set(props, "shouldRegisterLabelContainerRef", false);
      _.set(props, "registerLabelContainerRef", _.noop);

      const isMappedWithDetailsFields = _.get(
        fieldObj,
        "config.isMappedWithDetailsFields",
        false
      );

      if (isMappedWithDetailsFields) {
        if (
          _.has(updateParams, "startDate") &&
          _.has(updateParams, "endDate")
        ) {
          // if local state has start and end date then use it
          _.set(props, "value", {
            startDate: updateParams.startDate,
            endDate: updateParams.endDate,
          });
        } else {
          const mappedDetailsFields = _.get(
            fieldObj,
            "config.mappedDetailsFields",
            []
          );

          const newValue = getUnitPlanDetailsValueFromFieldKey({
            unitPlanDetails: unitPlanDetails,
            mappedDetailsFields,
            key: field,
          });

          _.set(props, "value", newValue);
        }
      }
      break;
    }

    default: {
      if (shouldInitOptions) {
        // if value and options are not same for it means that options has been changed
        // setting value to empty value will re-init the value
        if (_.size(_.intersectionBy(value, defaultOptions, "id")) <= 0) {
          _.set(props, "value", []);
        }
      }
    }
  }

  // modal for selection of datasets (standards, atls, etc)
  // need to open the "edit" modal on mount
  if (
    type === "PlannerElementDataSet" ||
    type === "PlannerElementDataSet_Ported"
  ) {
    _.set(props, "shouldOpenModalOnMount", true);
  }

  return props;
};

export const getIsDynamicField = params => {
  const { field, isUnitDependent, allFields } = params;

  const interdisciplinaryFieldUID = INTERDISCIPLINARY_FIELD_MAPPING[field];

  return (
    isUnitDependent &&
    getIsMYPInterdisciplinaryUnit(allFields) &&
    interdisciplinaryFieldUID
  );
};

export const getSubFields = params => {
  const { t, unitPlanFields, fieldList, field, unitPlanTemplate } = params;

  const isMYPInterdiscplinaryUnit = getIsMYPInterdisciplinaryUnit(
    unitPlanFields
  );

  const resolvedSubjectNodes = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "resolvedMinimalTree.subjects",
    []
  );

  if (isMYPInterdiscplinaryUnit && field === "disciplinaryContent") {
    return _.reduce(
      resolvedSubjectNodes,
      (acc, node) => {
        acc.push({
          config: {
            label: t("common:add_label1_for_label2_without_quotes", {
              label1: t("common:content_label"),
              label2: node?.name,
            }),
            subText: _.get(
              unitPlanTemplate,
              "field_list.disciplinaryContent.config.subtext"
            ),
          },
          key: node?.id,
          printType: "BaseComponent",
          type: "RichText",
          viewType: "TextView",
        });

        return acc;
      },
      []
    );
  }

  return _.get(fieldList?.[field], "subFields", []);
};

export const getEntityAndFieldType = params => {
  const { unitPlanDetails, config } = params;
  const { unitPlanFields } = unitPlanDetails || {};

  let { field, type } = params;

  if (_.includes(["title", "unitTitle"], field) && type === UNIT) {
    field = "unitPlan";
    type = "ASSESSMENT";
  } else if (
    field === "content" &&
    getIsMYPInterdisciplinaryUnit(unitPlanFields)
  ) {
    field = "disciplinaryContent";
  } else if (!params?.type && config?.type) {
    type = _.get(config, "type");
  }

  return { field, type };
};

const getUnitPlanFieldValue = ({ t, unitPlan }) => {
  if (_.isEmpty(unitPlan)) return { value: null };

  const { title = "Untitled", image } = unitPlan || {};
  const { value } = title || {};

  const imageUrl = getAttachmentUrlFromFieldValue({
    fieldValue: image,
  });

  const unitCoverImage = getDefaultUnitPlanImage({
    id: unitPlan?.id,
    imageUrl,
    getRethumb: image => `url(${image})`,
  });

  return {
    value: `<div style="display: flex; gap: 6px;">
            <div style="background: ${unitCoverImage} no-repeat center center / cover; border-radius: 4px; width: 24px; height: 24px; flex-shrink: 0;"></div>
            <span>${value || t("common:untitled")}</span>
          </div>`,
  };
};
export const getContextFieldValue = params => {
  const {
    t,
    field,
    type,
    course,
    config,
    context,
    assessmentDetails,
    unitPlanDetails,
    assessmentTemplate,
    unitPlanTemplate,
    curriculumProgramType,
  } = params;

  // handled separately, just for course grade
  if (field === "grades" && type === "COURSE") {
    return {
      value:
        _.get(course?.primaryGrade, "name") ||
        _.join(_.map(course?.grades, "name"), ", ") ||
        _.join(_.map(assessmentDetails?.grades, "name"), ", "),
    };
  } else if (field === "objectiveLevel" && type === UNIT) {
    const subjects = getResolvedMinimalTreeSet({
      fields: assessmentDetails?.allFields,
      uid: "subjects",
      curriculumType: curriculumProgramType,
    });

    const objectiveLevelNodes = getResolvedMinimalTreeSet({
      fields: unitPlanDetails?.unitPlanFields,
      uid: "objectiveLevel",
      curriculumType: curriculumProgramType,
    });

    const objectiveLevels = getMYPObjectiveLevelFromSubject({
      objectiveLevelNodes,
      subjects,
      showSubject: true,
    });

    if (!_.isEmpty(objectiveLevels)) {
      return { value: _.join(objectiveLevels, ", ") };
    }

    return { value: null };
  }

  const assessmentFields = _.get(assessmentDetails, "allFields", []);
  const unitPlanFields = _.get(unitPlanDetails, "unitPlanFields", []);

  const assessmentFieldList = _.get(assessmentTemplate, "field_list", {});
  const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});

  const assessmentFieldData = _.find(assessmentFields, { uid: field });
  const unitPlanFieldData = _.find(unitPlanFields, { uid: field });

  const assessmentFieldValue = _.get(assessmentFieldData, "value");
  const unitPlanFieldValue = _.get(unitPlanFieldData, "value");

  const subjectIds = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "value"
  );

  const valueKeys =
    type === ASSESSMENT ? assessmentFieldValue : unitPlanFieldValue;

  const fieldObj =
    type === ASSESSMENT ? assessmentFieldList[field] : unitPlanFieldList[field];

  const fieldType =
    _.get(fieldObj, "config.type") || _.get(fieldObj, "config.viewType");

  const resolvedValue =
    type === ASSESSMENT
      ? _.get(assessmentFieldData, "resolvedMinimalTree")
      : _.get(unitPlanFieldData, "resolvedMinimalTree");

  const value = getElementValue({
    t,
    fieldUID: field,
    valueKeys: valueKeys || context?.[field],
    unitPlanFieldValue,
    fieldObj: fieldObj || config,
    subjectIds: subjectIds,
    resolvedValue: resolvedValue || {},
    isUnitPlan: type === UNIT,
    unitPlanFields,
    subjects: _.get(
      _.find(unitPlanFields, { uid: "subjects" }),
      "resolvedMinimalTree.subjects",
      []
    ),
  });

  if (_.isArray(value) && !_.isEmpty(value)) {
    const selectedCount = t("common:lowercase", {
      text: t("common:label_seleceted", {
        count: _.size(valueKeys),
      }),
    });

    if (_.includes(SHOW_COUNT_FIELDS, field)) {
      // TODO: hook actual tooltip later

      // const tooltip = buildLearningGoalsHierarchy(
      //   getFlattenedNodes({
      //     nodes: value,
      //   })
      // );

      return { value: selectedCount };
    } else if (_.every(value, _.isString)) {
      return { value: _.join(value, ", ") };
    } else if (_.every(value, item => _.has(item, "value"))) {
      return { value: _.join(_.map(value, "value"), ", ") };
    } else {
      return { value: selectedCount };
    }
  } else {
    if (field === "inquiryQuestions") {
      const _value = _.flatten(_.values(value));
      if (!_.isEmpty(_value)) {
        return {
          value: t("common:lowercase", {
            text: t("common:label_seleceted", {
              count: _.size(_value),
            }),
          }),
        };
      } else return { value: null };
    } else if (field === "unitPlan") {
      return getUnitPlanFieldValue({
        t,
        unitPlan: context?.[field] || resolvedValue?.[field],
      });
    } else if (fieldType === "RichText") {
      return { value: _.trim(extractContentFromHTML({ htmlString: value })) };
    } else {
      return { value: _.trim(htmlToText(value)) };
    }
  }
};

export const updateIsRequired = fields => {
  const mypObjectives = _.filter(fields, ({ field, isRequired }) => {
    return isRequired && _.includes(MYP_OBJECTIVES, field);
  });

  const hasBothMYPObjectives = _.size(mypObjectives) === 2;

  if (hasBothMYPObjectives) {
    const objectivesMYP = _.find(fields, {
      field: "objectivesMYP",
    });

    const interdisciplinaryCriteriaMYP = _.find(fields, {
      field: "interdisciplinaryCriteriaMYP",
    });

    if (
      !_.isEmpty(objectivesMYP?.value) &&
      !_.isEmpty(interdisciplinaryCriteriaMYP?.value)
    ) {
      objectivesMYP.isExcluded = false;
      interdisciplinaryCriteriaMYP.isExcluded = false;
    } else if (!_.isEmpty(objectivesMYP?.value)) {
      if (interdisciplinaryCriteriaMYP?.isRequired) {
        interdisciplinaryCriteriaMYP.isRequired = false;
        interdisciplinaryCriteriaMYP.isExcluded = true;
      } else {
        interdisciplinaryCriteriaMYP.isExcluded = true;
      }
    } else if (!_.isEmpty(interdisciplinaryCriteriaMYP?.value)) {
      if (objectivesMYP?.isRequired) {
        objectivesMYP.isRequired = false;
        objectivesMYP.isExcluded = true;
      } else {
        objectivesMYP.isExcluded = false;
      }
    } else {
      objectivesMYP.isExcluded = false;
      interdisciplinaryCriteriaMYP.isExcluded = false;
    }
  }

  return fields;
};

export const getIsContextFieldDisabled = params => {
  const {
    field,
    config,
    middlePaneRef,
    isDisabled,
    assessmentTemplate,
  } = params;

  if (isDisabled || config?.isDisabled) return true;
  else {
    switch (field) {
      case "unitPlan": {
        return getIsUnitPlanFieldDisabled(params);
      }
      default: {
        const assessmentFieldList = _.get(assessmentTemplate, "field_list", {});
        const fieldObj = _.get(assessmentFieldList, field, {});

        return middlePaneRef?.getIsDisabled({
          fieldKey: field,
          fieldObj,
        });
      }
    }
  }
};

export const isPromptEqual = (
  arr1,
  arr2,
  keys = ["field", "type", "isRequired"]
) => {
  const transform = (array, keys) => _.map(array, item => _.pick(item, keys));

  const transformedArr1 = transform(arr1, keys);
  const transformedArr2 = transform(arr2, keys);
  return _.isEqual(_.sortBy(transformedArr1), _.sortBy(transformedArr2));
};

export const getPlaceholder = params => {
  const { t, value, selectedAction, isAITutor, isInitialState } = params;

  const trimmedQuery = _.trim(value);

  if (
    (_.isEmpty(selectedAction) && _.isEmpty(trimmedQuery)) ||
    _.isEmpty(trimmedQuery)
  ) {
    if (isAITutor) {
      if (isInitialState)
        return t("ai:start_by_defining_your_tutoring_objective_placeholder");
      else return t("ai:ai_tutor_chat_placeholder");
    } else return t("classRoom:start_typing_to_get_suggestions");
  } else {
    if (isAITutor) return t("ai:ai_tutor_prompt_selected_placeholder");
    else return t("ai:le_prompt_selected_placeholder");
  }
};

const getModalMinHeight = ({ fieldUID }) => {
  switch (fieldUID) {
    case "theme":
      return "220px";

    default:
      return null;
  }
};

export const getCustomModalStyle = ({
  styles,
  fieldType,
  fieldUID,
  isUnitDependent,
  isUnitPlanAI,
}) => {
  switch (fieldType) {
    case "FieldDropdown": {
      if (
        (fieldUID === "subjects" || fieldUID === "unitPlan") &&
        !isUnitDependent
      ) {
        return {
          ...styles,
          headerWithoutSubtextStyle: {
            ...styles.headerWithoutSubtextStyle,
            padding: "20px 16px 0 24px",
          },
          headerWithSubtextStyle: {
            ...styles.headerWithSubtextStyle,
            padding: "20px 16px 0 24px",
          },
          modalBodyStyle: {
            padding: fieldUID === "subjects" ? "0px" : "0px",
            overflow: "hidden",
          },
          modalFooterStyle: {
            padding: "20px 24px",
          },
        };
      }

      return {
        ...styles,
        headerWithoutSubtextStyle: {
          ...styles.headerWithoutSubtextStyle,
          padding: "20px 24px 0px",
        },
        headerWithSubtextStyle: {
          ...styles.headerWithSubtextStyle,
          padding: "20px 24px 0px",
        },
        modalBodyStyle: {
          padding: "0px",
          overflow: "hidden",
        },
      };
    }

    default: {
      const modalBodyStyle = {};
      const headerWithoutSubtextStyle = {
        ...styles.headerWithoutSubtextStyle,
        padding: "16px 24px 0",
      };

      if (isUnitPlanAI) {
        modalBodyStyle.padding = "16px 24px";
        headerWithoutSubtextStyle.padding = "16px 24px 8px";

        const minHeight = getModalMinHeight({ fieldUID });
        if (minHeight) {
          modalBodyStyle.minHeight = minHeight;
        }
      }

      return {
        ...styles,
        headerWithoutSubtextStyle,
        headerWithSubtextStyle: {
          ...styles.headerWithSubtextStyle,
          padding: "16px 24px 0",
        },
        modalBodyStyle,
      };
    }
  }
};

export const shouldUpdateSubject = unitPlanSubjectIds => {
  return !_.isEmpty(unitPlanSubjectIds) && _.size(unitPlanSubjectIds) === 1;
};
/**
 * @description generating object of default context fields by curriculum type
 * @param {string} curriculumProgramType - school's curriculum type
 * @param {boolean} isQuestionAssistant - worksheet questions's AI asssistant
 * @param {Function} t - utility function for localisation
 * @returns {Object} default context fields
 */
export const getDefaultContextFields = params => {
  const {
    t,
    isAITutor = false,
    curriculumProgramType,
    isBlankQuestion = false,
    isQuestionAssistant = false,
  } = params;

  const DEFAULT_CONTEXT_FIELDS = {
    [CURRICULUM_TYPE_PYP]: [
      {
        field: "grades",
        type: "COURSE",
        config: {
          label: "common:grade",
          isLocalised: true,
        },
        isRequired: true,
        isDisabled: true,
      },
      {
        field: "unitPlan",
        type: "ASSESSMENT",
        isRequired: false,
      },
      {
        field: "theme",
        type: "UNIT",
        isRequired: false,
      },
      {
        field: "centralIdea",
        type: "UNIT",
        isRequired: false,
      },
    ],
    [CURRICULUM_TYPE_MYP]: [
      {
        field: "grades",
        type: "COURSE",
        config: {
          label: "common:grade",
          isLocalised: true,
        },
        isRequired: true,
        isDisabled: true,
      },
      {
        field: "subjects",
        type: "ASSESSMENT",
        isRequired: true,
      },
      {
        field: "unitPlan",
        type: "ASSESSMENT",
        isRequired: false,
        shouldHide: isQuestionAssistant,
      },
      {
        field: "objectiveLevel",
        type: "UNIT",
        config: {
          label: "unitPlan:myp_objective_year_phase",
          isLocalised: true,
        },
        isRequired: false,
        isDisabled: true,
      },
      {
        field: "globalContextMYP",
        type: "UNIT",
        isRequired: false,
        shouldHide: isQuestionAssistant,
      },
      {
        field: "soi",
        type: "UNIT",
        isRequired: false,
        shouldHide: isQuestionAssistant,
      },
      {
        field: "mypContent",
        type: null,
        config: {
          isLocalField: true,
          label: t("worksheet:targeted_content"),
          placeholder: t("common:enter_with_label", {
            label: t("worksheet:targeted_content"),
          }),
          buttonLabel: t("common:enter_with_label", {
            label: t("worksheet:targeted_content"),
          }),
          emptyValueButtonLabel: t("common:enter_with_label", {
            label: t("worksheet:targeted_content"),
          }),
          modalLabel: t("common:enter_with_label", {
            label: t("worksheet:targeted_content"),
          }),
          fieldTypeConfig: {
            maxRows: 3,
          },
          shouldReset: true,
          modalInnerStyle: {
            minWidth: "100%",
          },
          modalStyle: {
            width: "488px",
          },
          type: "TextArea",
        },
        isRequired: isBlankQuestion,
        shouldHide: !isQuestionAssistant,
      },
    ],
    [CURRICULUM_TYPE_UBD]: [
      {
        field: "grades",
        type: "COURSE",
        config: {
          label: "common:grade",
          isLocalised: true,
        },
        isRequired: true,
        isDisabled: true,
      },
      {
        field: "unitPlan",
        type: "ASSESSMENT",
        isRequired: false,
      },
    ],
    [CURRICULUM_TYPE_DP]: [
      {
        field: "grades",
        type: "COURSE",
        config: {
          label: "common:grade",
          isLocalised: true,
        },
        isRequired: true,
        isDisabled: true,
      },
      {
        field: "subjectLevel",
        type: "ASSESSMENT",
        isRequired: !isAITutor,
      },
      {
        field: "subjects",
        type: "ASSESSMENT",
        isRequired: true,
        isDisabled: true,
      },
      {
        field: "unitPlan",
        type: "ASSESSMENT",
        isRequired: false,
        shouldHide: isQuestionAssistant,
      },
    ],
  };

  const defaultContextFields = DEFAULT_CONTEXT_FIELDS[curriculumProgramType];
  return _.filter(
    defaultContextFields,
    ({ shouldHide = false }) => !shouldHide
  );
};

export const buildLearningGoalsHierarchy = (
  items,
  parent = null,
  level = 0
) => {
  // filter parents for current depth level
  const filteredItems = _.filter(items, { parent });
  return _.map(filteredItems, item => {
    // recursively append children nodes
    const children = !_.isNil(_.filter(items, { parent: item.id }))
      ? `\n${buildLearningGoalsHierarchy(items, item.id, level + 1)}`
      : "";

    return `${_.repeat(" ", level * 6)}- ${item.label}${children}`;
  }).join("");
};

export const moveCaretToEnd = ref => {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(ref);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const moveCaretToEndOfContentEditable = ref => {
  if (!ref) return;

  setTimeout(() => {
    const editableSpans = ref.querySelectorAll("span[contenteditable='true']");
    const lastEditableSpan = _.first(editableSpans); // focus on first editable span

    if (lastEditableSpan) {
      const range = document.createRange();
      const selection = window.getSelection();

      // Move the caret to the end of the contentEditable span
      range.selectNodeContents(lastEditableSpan);
      range.collapse(false); // Collapses the range to the end of the node
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      moveCaretToEnd(ref);
    }
  }, 0);
};

export const getPromptLabelWithAttachments = ({
  t,
  promptLabel,
  attachments,
}) => {
  if (_.isEmpty(attachments)) return promptLabel;

  // remove initial 'insert attachment' btn
  const tempElement = document.createElement("div");
  tempElement.innerHTML = promptLabel;
  const attachmentElement = tempElement.querySelector("#attachment-text");
  if (!attachmentElement) return promptLabel;

  const attachmentsInSpan = _.reduce(
    attachments,
    (updatedLabel, attachment, index) => {
      return wrapPlaceholderWithSpan({
        t,
        label: `${updatedLabel} ##${attachment.name}##`,
        attachmentId: attachment.id,
        showEditIcon: true,
        removeRightMargin: index === _.size(attachments) - 1,
      });
    },
    ""
  );

  // Replace the entire attachmentElement with the new HTML content
  attachmentElement.outerHTML = attachmentsInSpan;

  return tempElement.innerHTML;
};

export const getInsertAttachmentElement = selectedAction => {
  const originalPromptLabel = _.get(selectedAction, "label", "");
  const tempElement = document.createElement("div");
  tempElement.innerHTML = originalPromptLabel;
  return tempElement.querySelector("#attachment-text");
};

const isPlaceholderSpanSame = (originalHTML, modifiedHTML) => {
  const originalContainer = document.createElement("div");
  const modifiedContainer = document.createElement("div");

  originalContainer.innerHTML = originalHTML;
  modifiedContainer.innerHTML = modifiedHTML;

  const originalSpans = originalContainer.querySelectorAll(
    "#content-editable-placeholder"
  );
  const modifiedSpans = modifiedContainer.querySelectorAll(
    "#content-editable-placeholder"
  );

  if (_.size(originalSpans) !== _.size(modifiedSpans)) return false;

  return _.some(originalSpans, (span, idx) => {
    const originalPlaceholderText = span?.textContent?.trim();
    const modifiedPlaceholderText = modifiedSpans?.[idx]?.textContent?.trim();

    return (
      originalPlaceholderText === modifiedPlaceholderText ||
      _.isEmpty(modifiedPlaceholderText)
    );
  });
};

export const getIsSendButtonDisabled = params => {
  const { value, isAITutor, selectedAction, isAttachmentUploading } = params;

  // empty spaces, new lines are represented as "&nbsp;", "<br>", "\n" in contenteditable
  // so simple trim will not work, hence we need to convert the html to text and then trim
  const isQueryEmpty = _.isEmpty(
    _.trim(
      htmlToText(value, {
        preserveNewlines: false,
        wordwrap: null,
      })
    )
  );

  return (
    (_.isEmpty(selectedAction) && isQueryEmpty) || // if no prompt is selected or no input has been given
    isAttachmentUploading || // if attachment is uploading
    (isAITutor &&
      !_.isEmpty(selectedAction) &&
      isPlaceholderSpanSame(selectedAction?.label, value))
  );
};

/**
 * This function segregates into different fields based on the parent subject
 * from: {"learningStandardMYP": ["12345", "67890"]}
 * to: {"disciplinaryLearningStandardMYP_12345": ["12345"], "disciplinaryLearningStandardMYP_67890": ["67890"]}
 */
export const getGroupedUnitPlanUpdateParams = ({
  params,
  extraParams,
  updateParams,
}) => {
  const { nodes } = extraParams;

  const fieldKey = _.chain(params).keys().first().value();
  const value = _.get(params, fieldKey);

  const dynamicFieldIds = _.keys(updateParams);

  const segregatedParams = _.reduce(
    value,
    (result, nodeId) => {
      const node = _.find(nodes, { id: nodeId }, {});
      const { dataSetNodeWrapper } = node || {};

      // there is always 1:1 mapping
      const parentSubject = _.get(
        dataSetNodeWrapper,
        "properties.subjects.[0]",
        []
      );

      const parentId = _.get(parentSubject, "id", null);
      if (!parentId) return result;

      const dynamicFieldId = _.find(dynamicFieldIds, id =>
        _.includes(id, parentId)
      );
      if (!dynamicFieldId) return result;

      if (!_.has(result, dynamicFieldId)) {
        result[dynamicFieldId] = [];
      }

      result[dynamicFieldId].push(nodeId);
      return result;
    },
    {}
  );

  // some dynamic fields may have no values
  // add them back in the final result
  _.forEach(dynamicFieldIds, fieldId => {
    if (!_.has(segregatedParams, fieldId)) {
      segregatedParams[fieldId] = [];
    }
  });

  // params should always have the original field key
  // only used for displaying how many items are selected
  segregatedParams[fieldKey] = value;

  return segregatedParams;
};

export const getOptionsForPortedFields = ({
  field,
  fieldKey,
  curriculumType,
  unitPlanFields,
  courseId,
  mode,
  curriculumId,
  objectiveLevelNodes,
  grades,
  academicYearId,
  organizationId,
  fieldObj,
  learningCourseId,
  plannerElementNodeSets,
  plannerElementConfigType,
  t,
  field_listV2,
  defaultOptions,
  readOnly,
}) => {
  if (field === "subjects" || field === "subjectLevel") {
    return getOptionsForUnitPlanDetailsField({
      fieldKey,
      curriculumType,
      unitPlanFields,
      courseId,
      mode,
      curriculumId,
      objectiveLevelNodes,
      grades,
      academicYearId,
      organizationId,
      learningCourseId,
      readOnly,
    });
  } else {
    return getPlannerFieldV2Options({
      grades,
      fieldObj,
      fieldKey,
      unitPlanFields,
      learningCourseId,
      plannerElementNodeSets,
      plannerElementConfigType,
      t,
      objectiveLevelNodes,
      curriculumType,
      field_listV2,
      organizationId,
      defaultOptions,
      courseId,
    });
  }
};
